import { useState, type FormEvent } from 'react';
import { Pencil, Plus, Star, Trash2, X } from 'lucide-react';
import Field from '../../components/admin/Field';
import ImageUploader from '../../components/admin/ImageUploader';
import { EmptyState, ErrorState, Loading } from '../../components/StatusStates';
import {
  useAdminProjects,
  useCreateProject,
  useDeleteProject,
  useUpdateProject,
} from '../../hooks/use-admin';
import { ApiError } from '../../api/client';
import type { Project, ProjectPayload } from '../../api/types';
import { tagsToText, textToTags } from '../../lib/format';

interface FormState {
  name: string;
  description: string;
  tagsText: string;
  url: string;
  sortOrder: number;
  featured: boolean;
  coverImage: string | null;
}

const EMPTY_FORM: FormState = {
  name: '',
  description: '',
  tagsText: '',
  url: '',
  sortOrder: 0,
  featured: false,
  coverImage: null,
};

function projectToForm(project: Project): FormState {
  return {
    name: project.name,
    description: project.description ?? '',
    tagsText: tagsToText(project.tags),
    url: project.url ?? '',
    sortOrder: project.sortOrder,
    featured: project.featured,
    coverImage: project.coverImage,
  };
}

export default function AdminProjects() {
  const projectsQuery = useAdminProjects();
  const deleteMutation = useDeleteProject();
  const [editing, setEditing] = useState<{ id: number | null; project: Project | null } | null>(
    null,
  );

  const handleDelete = async (project: Project) => {
    if (!window.confirm(`确定删除项目「${project.name}」吗？此操作不可恢复。`)) return;
    try {
      await deleteMutation.mutateAsync(project.id);
      if (editing?.id === project.id) setEditing(null);
    } catch (error) {
      window.alert(error instanceof Error ? error.message : '删除失败');
    }
  };

  return (
    <>
      <div className="toolbar">
        <div className="toolbar-spacer" />
        <button
          type="button"
          className="btn btn-primary btn-sm"
          onClick={() => setEditing({ id: null, project: null })}
        >
          <Plus size={15} aria-hidden="true" />
          新建项目
        </button>
      </div>

      {editing && (
        <ProjectFormPanel
          editingId={editing.id}
          initial={editing.project ? projectToForm(editing.project) : EMPTY_FORM}
          onClose={() => setEditing(null)}
        />
      )}

      <div className="table-panel">
        {projectsQuery.isLoading ? (
          <Loading />
        ) : projectsQuery.isError ? (
          <ErrorState onRetry={() => projectsQuery.refetch()} />
        ) : (projectsQuery.data ?? []).length === 0 ? (
          <EmptyState title="暂无项目" description="点击右上角「新建项目」添加作品。" />
        ) : (
          <table className="data-table">
            <thead>
              <tr>
                <th>排序</th>
                <th>名称</th>
                <th>标签</th>
                <th>精选</th>
                <th>地址</th>
                <th style={{ textAlign: 'right' }}>操作</th>
              </tr>
            </thead>
            <tbody>
              {projectsQuery.data?.map((project) => (
                <tr key={project.id}>
                  <td>{project.sortOrder}</td>
                  <td className="col-title">{project.name}</td>
                  <td>{project.tags.join('、') || '—'}</td>
                  <td>
                    {project.featured && (
                      <span
                        className="icon-inline"
                        style={{ color: 'var(--color-warning)' }}
                      >
                        <Star size={13} fill="currentColor" aria-hidden="true" />
                        精选
                      </span>
                    )}
                  </td>
                  <td
                    style={{
                      maxWidth: 220,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {project.url ? (
                      <a href={project.url} target="_blank" rel="noopener noreferrer">
                        {project.url}
                      </a>
                    ) : (
                      '—'
                    )}
                  </td>
                  <td>
                    <div className="table-actions" style={{ justifyContent: 'flex-end' }}>
                      <button
                        type="button"
                        className="btn btn-outline btn-sm"
                        onClick={() => setEditing({ id: project.id, project })}
                      >
                        <Pencil size={13} aria-hidden="true" />
                        编辑
                      </button>
                      <button
                        type="button"
                        className="btn btn-danger btn-sm"
                        onClick={() => void handleDelete(project)}
                      >
                        <Trash2 size={13} aria-hidden="true" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </>
  );
}

interface ProjectFormPanelProps {
  editingId: number | null;
  initial: FormState;
  onClose: () => void;
}

function ProjectFormPanel({ editingId, initial, onClose }: ProjectFormPanelProps) {
  const createMutation = useCreateProject();
  const updateMutation = useUpdateProject(editingId ?? -1);
  const [form, setForm] = useState<FormState>(initial);
  const [formError, setFormError] = useState<string | null>(null);
  const [errors, setErrors] = useState<Record<string, string>>({});

  const update = (patch: Partial<FormState>) => setForm((current) => ({ ...current, ...patch }));

  const validate = () => {
    const next: Record<string, string> = {};
    if (!form.name.trim()) next.name = '项目名称不能为空';
    if (form.url && !/^https?:\/\/.+/i.test(form.url)) next.url = '请输入以 http(s) 开头的合法地址';
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();
    setFormError(null);
    if (!validate()) return;

    const payload: ProjectPayload = {
      name: form.name.trim(),
      description: form.description.trim() || null,
      tags: textToTags(form.tagsText),
      url: form.url.trim() || null,
      coverImage: form.coverImage,
      sortOrder: Number.isFinite(form.sortOrder) ? form.sortOrder : 0,
      featured: form.featured,
    };

    try {
      if (editingId === null) {
        await createMutation.mutateAsync(payload);
      } else {
        await updateMutation.mutateAsync(payload);
      }
      onClose();
    } catch (error) {
      setFormError(error instanceof ApiError ? error.message : '保存失败，请稍后重试');
    }
  };

  const submitting = createMutation.isPending || updateMutation.isPending;

  return (
    <section className="panel-form" style={{ marginBottom: '1.5rem' }}>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginBottom: '1.25rem',
        }}
      >
        <h2 style={{ fontSize: '1.05rem' }}>{editingId === null ? '新建项目' : '编辑项目'}</h2>
        <button type="button" className="btn btn-ghost btn-sm" onClick={onClose} aria-label="关闭表单">
          <X size={16} />
        </button>
      </div>

      <form onSubmit={handleSubmit}>
        <div className="form-grid">
          <Field label="项目名称" required error={errors.name}>
            <input
              className={`form-input ${errors.name ? 'invalid' : ''}`}
              value={form.name}
              maxLength={150}
              onChange={(event) => update({ name: event.target.value })}
            />
          </Field>
          <Field label="排序值" hint="数值越小越靠前">
            <input
              type="number"
              className="form-input"
              value={form.sortOrder}
              min={0}
              onChange={(event) => update({ sortOrder: Number(event.target.value) })}
            />
          </Field>
          <Field label="项目简介" className="form-field-full">
            <textarea
              className="form-textarea"
              rows={3}
              maxLength={1000}
              value={form.description}
              onChange={(event) => update({ description: event.target.value })}
            />
          </Field>
          <Field label="技术标签" hint="多个标签以逗号或顿号分隔">
            <input
              className="form-input"
              value={form.tagsText}
              placeholder="React, TypeScript"
              onChange={(event) => update({ tagsText: event.target.value })}
            />
          </Field>
          <Field label="外部地址" hint="代码仓库或在线演示链接" error={errors.url}>
            <input
              className={`form-input ${errors.url ? 'invalid' : ''}`}
              value={form.url}
              placeholder="https://github.com/..."
              onChange={(event) => update({ url: event.target.value })}
            />
          </Field>
          <Field label="封面图" className="form-field-full">
            <ImageUploader value={form.coverImage} onChange={(url) => update({ coverImage: url })} />
          </Field>
          <Field label="首页精选" className="form-field-full">
            <label
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '0.5rem',
                fontSize: 'var(--font-size-sm)',
              }}
            >
              <input
                type="checkbox"
                checked={form.featured}
                onChange={(event) => update({ featured: event.target.checked })}
                style={{ width: 16, height: 16 }}
              />
              在首页精选区域展示该项目
            </label>
          </Field>
        </div>

        {formError && (
          <div className="inline-alert inline-alert-error" style={{ marginTop: '1rem' }}>
            {formError}
          </div>
        )}

        <div className="form-actions">
          <button type="submit" className="btn btn-primary" disabled={submitting}>
            {editingId === null ? '创建项目' : '保存修改'}
          </button>
        </div>
      </form>
    </section>
  );
}
